import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import {
  BooleanInput,
  Datagrid,
  DateField,
  Edit,
  EditButton,
  FormDataConsumer,
  FunctionField,
  Labeled,
  NumberField,
  NumberInput,
  Pagination,
  ReferenceField,
  ReferenceManyField,
  TabbedForm,
  TextField,
  TextInput,
  useDataProvider,
  useRefresh,
  useRecordContext,
  SaveButton,
  Toolbar,
  // SimpleShowLayout
} from "react-admin";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField as MuiTextField,
  Typography
} from "@mui/material";
import { CopyBlock, dracula } from "react-code-blocks";

import { NotesIterator } from 'custom_components/notes';
import WidthContextProvider, { WidthContext } from "custom_components/WidthProvider";
import StoreMapComponent from './components/MapListMatch';
import UpsertButton from './components/UpsertButton';
import { parsePosSettings, getSettingsForPosType, PosSettingsFields } from "./utils";
import { restClient } from "../../../transport-layer/src";
import { useLocation, useNavigate } from "react-router-dom";
import { AutocompleteInput } from "react-admin";


const MasterCustomerLink = () => {
  const record = useRecordContext();
  const navigate = useNavigate();
  if (!record?.infobate_master_customer_id) return null;
  return (
    <Box sx={{ mt: 1, textAlign: 'center' }}>
      <Button
        variant="outlined"
        onClick={() => navigate(`/onpoint_master_customers/${record.infobate_master_customer_id}`)}
      >
        Master Customer<br />id: {record.infobate_master_customer_id}
      </Button>
    </Box>
  );
};

const Aside = () => {
  const record = useRecordContext();
  return  (
  <div style={{ width: 200, margin: '1em' }}>
      <MasterCustomerLink />
      <Typography variant="h6" sx={{ mt: 1 }}>Notes</Typography>
      <ReferenceManyField
          record={record}
          target="patron_points_store_id"
          reference="onpoint_patron_points_store_notes"
          sort={{ field: "date_added", order: "DESC" }}
        >
          <NotesIterator showStatus reference="onpoint_patron_points_store"  foreignKeyMapping={'patron_points_store_id'} record={record}/>
        </ReferenceManyField>

  </div>
)};

const FilePanel = (props) => {
  // return JSON.stringify(props?.record?.files)
  return (
    <List id="s3-file-list" style={{ maxHeight: 400, overflowY: "auto" }}>
      {props?.record?.files.map((file, i) => (
        // <ListItem
        //   key={i}
        //   id={'s3-file-' + i}
        //   alignItems="flex-start"
        // >
        //   <ListItemText
        //     primary={file}
        //     secondary={<span></span>}
        //   />
        // </ListItem>
        <ListItem
          button
          component="a"
          // href={`s3://transaction-collector-data/${file}`}
          href={`https://transaction-collector-data.s3.amazonaws.com/${file}`}
        >
          <ListItemText primary={file} />
        </ListItem>
      ))}
    </List>
  );
};


const PosSettings = ({ formData }) => {
  const record = useRecordContext();
  const posTypeId = formData?.pos_type_id;

  const posSettings = useMemo(() =>
    PosSettingsFields.find(typeObj => typeObj.id === posTypeId),
  [posTypeId]);

  const posSettingsDefaults = useMemo(() => (
    posSettings?.defaultValues ?? {}
  ), [posSettings]);

  // Parse pos_settings from record and extract the relevant nested settings for this pos_type
  const existingValues = useMemo(() => {
    const parsed = parsePosSettings(record?.pos_settings);
    return getSettingsForPosType(parsed, posTypeId);
  }, [record?.pos_settings, posTypeId]);

  // Merge: existing values take priority, defaults fill in blanks
  const mergedValues = useMemo(() => {
    const merged = {};
    for (let field in posSettingsDefaults) {
      const existing = existingValues[field];
      merged[field] = (existing !== undefined && existing !== null && existing !== '')
        ? existing
        : posSettingsDefaults[field];
    }
    return merged;
  }, [posSettingsDefaults, existingValues]);

  if (!posSettings) return 'N/A';

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1em' }}>
      {Object.keys(mergedValues).map((key, i) => (
        <TextInput
          source={`store_settings.pos_settings.0.${key}`}
          defaultValue={mergedValues[key]}
          label={key}
          key={`${posTypeId}-${key}`}
          disabled
        />
      ))}
    </Box>
  );
}

const MasterStoreEditButton = () => {
  const record = useRecordContext();

  return (
    <Fragment>
      {record && record.infobate_master_customer_id && (
        <EditButton
          resource='onpoint_master_customers'
          record={{ id: record.infobate_master_customer_id }}
          variant="outlined"
        />
      )}
    </Fragment>
  );
}

const Title = () => {
  const record = useRecordContext()
  return <span>{record ? `${record.name}` : ""}</span>;
};

const ConditionalToolbar = () => {
  const { pathname } = useLocation();
  const disableSave = useMemo(() =>
    pathname.includes('upsert-store'),
  [pathname]);

  return disableSave ? null : (
    <Toolbar>
      <SaveButton />
    </Toolbar>
  );
}

const OnPointScheduleSelect = ({ patronId, value, onChange }) => {
  const dataProvider = useDataProvider();
  const [schedules, setSchedules] = useState([]);

  useEffect(() => {
    if (!patronId) return;
    dataProvider.getList('patron_points_onpoint_participant_schedules', {
      filter: { patron_id: patronId },
      sort: { field: 'schedule', order: 'DESC' },
      pagination: { page: 1, perPage: 100 }
    }).then(({ data }) => {
      setSchedules(data.map(row => row.schedule));
    }).catch(err => console.error('Error fetching onpoint schedules:', err));
  }, [patronId, dataProvider]);

  return (
    <FormControl size="small" sx={{ minWidth: 240, mt: 1 }}>
      <InputLabel>Schedule</InputLabel>
      <Select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        label="Schedule"
      >
        {schedules.map((schedule, i) => (
          <MenuItem key={i} value={schedule}>{schedule}</MenuItem>
        ))}
        <MenuItem value="__new__">
          New Schedule ({new Date().toLocaleString()})
        </MenuItem>
      </Select>
    </FormControl>
  );
};

const OnPointSection = ({ formData, onPointSchedule, selectedSchedule, onScheduleChange }) => {
  const record = useRecordContext();
  const isBeingTurnedOn = formData.onpoint_participating && !record?.onpoint_participating;

  return (
    <Stack sx={{ gap: '0.5em' }}>
      <BooleanInput source="onpoint_participating" label="OnPoint" />
      {record?.onpoint_participating && formData.onpoint_participating && onPointSchedule.length > 0 && (
        <Box sx={{ pl: 2 }}>
          <Typography variant="caption" color="text.secondary">
            OnPoint Start Time:
          </Typography>
          {onPointSchedule.map((date, idx) => (
            <Typography key={idx} variant="body2">
              {new Date(date).toLocaleString()}
            </Typography>
          ))}
        </Box>
      )}
      {isBeingTurnedOn && (
        <OnPointScheduleSelect
          patronId={formData.id}
          value={selectedSchedule}
          onChange={onScheduleChange}
        />
      )}
    </Stack>
  );
};

const STORE_LOGS_LAMBDA_URL = process.env.REACT_APP_STORE_LOGS_URL;

const getDateString = (daysAgo = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().split('T')[0];
};

const LOG_COLUMNS = [
  { field: 'insert_date', label: 'Date' },
  { field: 'participant_name', label: 'Participant' },
  { field: 'level', label: 'Level' },
  { field: 'message', label: 'Message' },
];

const StoreLogsTab = () => {
  const record = useRecordContext();
  const [useDateRange, setUseDateRange] = useState(false);
  const [lookbackDays, setLookbackDays] = useState(30);
  const [startDate, setStartDate] = useState(() => getDateString(30));
  const [endDate, setEndDate] = useState(() => getDateString(0));
  const [limit, setLimit] = useState(1000);
  const [levelInfo, setLevelInfo] = useState(false);
  const [levelError, setLevelError] = useState(false);
  const [messageFilter, setMessageFilter] = useState('');
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [statusText, setStatusText] = useState('');
  const [sortField, setSortField] = useState('insert_date');
  const [sortDir, setSortDir] = useState('desc');
  const [columnFilters, setColumnFilters] = useState({});

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedLogs = useMemo(() => {
    if (!logs) return [];
    const filtered = logs.filter(row =>
      LOG_COLUMNS.every(col => {
        const f = columnFilters[col.field];
        if (!f) return true;
        return String(row[col.field] ?? '').toLowerCase().includes(f.toLowerCase());
      })
    );
    return [...filtered].sort((a, b) => {
      const aVal = a[sortField] ?? '';
      const bVal = b[sortField] ?? '';
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [logs, sortField, sortDir, columnFilters]);

  const fetchLogs = async () => {
    if (!record?.id) return;
    setLoading(true);
    setError(null);
    try {
      const levels = [];
      if (levelInfo) levels.push('INFO');
      if (levelError) levels.push('ERROR');
      const resolvedStart = useDateRange ? startDate : getDateString(lookbackDays);
      const resolvedEnd = useDateRange ? endDate : getDateString(0);
      const body = {
        store_id: record.id,
        startDate: `${resolvedStart}T00:00:00.000Z`,
        endDate: `${resolvedEnd}T23:59:59.999Z`,
        limit: Number(limit),
        order: 'DESC',
        ...(levels.length > 0 && { levels }),
        ...(messageFilter && { messageFilter }),
      };
      const response = await fetch(STORE_LOGS_LAMBDA_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.status) {
        setLogs(data.messages || []);
        setStatusText(data.statustext || '');
      } else {
        setError('Failed to fetch logs: ' + (data.statustext || 'Unknown error'));
      }
    } catch (e) {
      setError('Error fetching logs: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 1, width: '100%' }}>
      {/* Filter controls */}
      <FormControlLabel
        control={<Checkbox size="small" checked={useDateRange} onChange={(e) => setUseDateRange(e.target.checked)} />}
        label="Use date range"
        sx={{ mb: 1 }}
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'flex-end' }}>
        {useDateRange ? (
          <>
            <MuiTextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <MuiTextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
          </>
        ) : (
          <MuiTextField
            label="Days"
            type="number"
            value={lookbackDays}
            onChange={(e) => setLookbackDays(Number(e.target.value))}
            size="small"
            sx={{ width: 80 }}
            inputProps={{ min: 1 }}
          />
        )}
        <MuiTextField
          label="Limit"
          type="number"
          value={limit}
          onChange={(e) => setLimit(e.target.value)}
          size="small"
          sx={{ width: 100 }}
          inputProps={{ min: 1, max: 10000 }}
        />
        <MuiTextField
          label="Message Filter"
          value={messageFilter}
          onChange={(e) => setMessageFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          placeholder="Filter messages..."
        />
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Levels</Typography>
          <Box sx={{ display: 'flex', gap: 0 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={levelInfo} onChange={(e) => setLevelInfo(e.target.checked)} />}
              label="INFO"
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={levelError} onChange={(e) => setLevelError(e.target.checked)} />}
              label="ERROR"
            />
          </Box>
        </Box>
        <Button variant="contained" onClick={fetchLogs} disabled={loading} sx={{ height: 40 }}>
          {loading && <CircularProgress size={16} sx={{ mr: 1, color: 'inherit' }} />}
          Fetch Logs
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {logs !== null && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {statusText}
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {LOG_COLUMNS.map(col => (
                    <TableCell key={col.field} sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={sortField === col.field}
                        direction={sortField === col.field ? sortDir : 'asc'}
                        onClick={() => handleSort(col.field)}
                      >
                        {col.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  {LOG_COLUMNS.map(col => (
                    <TableCell key={col.field} sx={{ py: 0.5, bgcolor: 'background.paper' }}>
                      <MuiTextField
                        size="small"
                        placeholder={`Filter...`}
                        value={columnFilters[col.field] ?? ''}
                        onChange={(e) => setColumnFilters(prev => ({ ...prev, [col.field]: e.target.value }))}
                        inputProps={{ style: { fontSize: 12, padding: '4px 6px' } }}
                        sx={{ width: '100%' }}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedLogs.map((row, i) => (
                  <TableRow key={row.log4net_id ?? i} hover>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {new Date(row.insert_date).toLocaleString()}
                    </TableCell>
                    <TableCell>{row.participant_name}</TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ color: row.level === 'ERROR' ? 'error.main' : 'inherit' }}
                      >
                        {row.level}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 600, wordBreak: 'break-word' }}>
                      {row.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

const PatronPointsStoreEdit = (props) => {
  const dataProvider = useDataProvider();
  const refresh = useRefresh();
  const [useManualInputComponent, setUseManualInputComponent] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState('');

  async function checkValidMsid(data) {
    // console.log(data, restClient)
    return await restClient.getOne(`onpoint_master_customers`, {
      id: data,
    }).then((e) => {
      return typeof e.data === "object";
    }).catch(() => {
      return false
    });
  }
   
  const validateMsid = async (values) => {
    const errors = {};
    const validMsid = await checkValidMsid(values);
    if (!validMsid) {
      errors.infobate_master_customer_id = 'MSID not valid';
    }
    return errors.infobate_master_customer_id;
  };

  const updateStoreLink = useCallback(
    async (store) => {
      await dataProvider // needs to be async to refresh
        .update(`onpoint_patron_points_stores`, {
          id: store.patron_points_store_id,
          data: {id: store.patron_points_store_id, infobate_master_customer_id: store.id}
        })
        .catch((e) => {
          // window.location.href = '#/'
        });
      refresh();
    },
    [dataProvider, refresh]
  );

  return (
    <Edit title={<Title />} {...props} aside={<Aside/>}>
      <TabbedForm toolbar={<ConditionalToolbar />} syncWithLocation>
        <TabbedForm.Tab label="Store Data">
          <Box sx={{ display: 'flex', flexFlow: 'row wrap', gap: '.5em' }}>
            <TextInput source="id" label="ID" disabled />
            <TextInput source="name" label="Store Name" disabled />
            
            <Labeled label="Location Data" path="location-data">
              <Stack>
                <TextInput source="formatted_address" label="Formatted Address" fullWidth disabled />
                <NumberInput source="latitude" disabled />
                <NumberInput source="longitude" disabled />
              </Stack>
            </Labeled>
          </Box>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Store Settings" path="store-settings">
          <FunctionField label="Raw Source" source="store_settings" render={record => {
            return <CopyBlock
              text={(record.store_settings) ? JSON.stringify(record.store_settings, null, "\t") : ''}
              language={'json'}
              showLineNumbers={true}
              startingLineNumber={0}
              wrapLines
              theme={dracula}
            /> 
          }}/>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Monitor" path="monitor">
          <Labeled label="S3 Summary">
            <Box sx={{ display: 'flex', flexFlow: 'row wrap' }}>
              <ReferenceField
                reference="onpoint_patron_point_store_migration_summary"
                source="id"
                label="Migration Status"
                readOnly
              >
                <TextField source="status" />
              </ReferenceField>

              <ReferenceField
                reference="onpoint_patron_point_store_migration_summary"
                source="id"
                label="Last API File Date"
                readOnly
              >
                <DateField source="last_reported_api_file_date" />
              </ReferenceField>
              
              <ReferenceField
                reference="onpoint_patron_point_store_migration_summary"
                source="id"
                label="Last Legacy Reporting Date"
                readOnly
              >
                <DateField source="gavant_last_reported" />
              </ReferenceField>

              <ReferenceField
                reference="onpoint_patron_point_store_migration_summary"
                source="id"
                label="Days Behind"
                readOnly
              >
                <NumberField source="days_behind" />
              </ReferenceField>
            </Box>
          </Labeled>


          <ReferenceManyField
            fullWidth
            label="File Summary"
            reference={`onpoint_patron_points_tc_store_pos_type_day_summary`}
            target="patron_points_store_id"
            perPage={10}
            pagination={<Pagination />}
            sort={{ field: "last_modified_day", order: "DESC" }}
          >
            <Datagrid expand={<FilePanel />}>
              <TextField source="patron_points_store_id" label="Store ID" />
              <TextField source="pos_type" label="POS Type" />
              <TextField source="last_modified_day" label="Last Modified" />
              <TextField source="files_per_day" label="Files Per Day" />
            </Datagrid>
          </ReferenceManyField>

          <Labeled label="Store File Summary">
            <Stack>
              <TextField source="reported_pos_type" label="Reported POS Type" />
              <DateField source="last_reported" label="Last Reported" />
              <TextField source="file_count" />
            </Stack>
          </Labeled>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Match Store" path="match-store">
          <Box sx={{ display: 'flex', flexFlow: 'row wrap', gap: '1em' }}>
            <Labeled label="Patron Points Data">
              <Stack>
                <TextInput source="name" label="Patron Points Store Name" disabled />
                <TextInput source="id" label="Patron Points ID" disabled />
                <TextInput source="formatted_address" label="Patron Points Formatted Address"
                  disabled fullWidth />
              </Stack>
            </Labeled>

            <Labeled label="InfoBate Store Data">
              <Stack>
                <TextInput source='infobate_master_customer_id' label="MSID" disabled />
                <MasterStoreEditButton />
                <FormControlLabel
                  label="Manually input Infobate MSID"
                  control={
                    <Checkbox
                      key='store_match_control_toggle'
                      name='store_match_control_toggle'
                      checked={useManualInputComponent}
                      onChange={(e) => setUseManualInputComponent(e.target.checked)}
                      style={{ transform: "scale(1.25)" }}
                    />
                  }
                />
              </Stack>
            </Labeled>
          </Box>


          <WidthContextProvider>
            <WidthContext.Consumer>
              {(width) => {
                return (
                  <FormDataConsumer>
                    {({ formData, ...rest }) =>  (!useManualInputComponent) ? (
                      <StoreMapComponent
                        {...props}
                        fromStoreID={formData.id}
                        existingMatchID={[formData.infobate_master_customer_id]}
                        focusMarkerColor="#ED3A2C"
                        possibleMatchMarkerColor="#55790087"
                        matchedColor="#85BC00"
                        width={width}
                        height={250}
                        name="patron_points_id"
                        title={"Possible InfoBate Matches"}
                        enableMatching={true}
                        storeSearchEndpoint="onpoint_find_patron_point_to_infobate_matches"
                        storeSearchEndpointID="_patron_points_store_id"
                        focusStoreMarker={[
                          parseFloat(formData?.latitude),
                          parseFloat(formData?.longitude),
                        ]}
                        resourceLocation={"onpoint_master_customers"}
                        onChange={(data) => {
                          updateStoreLink(data);
                        }}
                      />
                    ) : (
                      <TextInput
                        source="infobate_master_customer_id"
                        label="InfoBate Master Store ID"
                        validate={validateMsid}
                      />
                    )}
                  </FormDataConsumer>
                );
              }}
            </WidthContext.Consumer>
          </WidthContextProvider>
        </TabbedForm.Tab>

        <TabbedForm.Tab label="Upsert Store" path="upsert-store">
          <Stack>
            <Labeled label="Last Updated" sx={{ marginBottom: '1em' }}>
              <ReferenceField
                source="id"
                reference="onpoint_patron_points_daily_stores_updated_times"
              >
                <DateField source="updated_at" showTime />
              </ReferenceField>
            </Labeled>

            <Box sx={{ display: 'flex', gap: '1em', flexFlow: 'row wrap' }}>
              <Stack>
                {/* POS Data */}
                <Labeled label="POS Data">
                  <Box sx={{ display: 'flex', flexFlow: 'row wrap' }}>
                    <TextInput
                      source="reported_pos_type"
                      label="Last Reported POS Type"
                      readOnly
                    />

                    <AutocompleteInput
                      source="pos_type_id"
                      label="POS Type"
                      choices={PosSettingsFields}
                      optionText="label"
                      readOnly
                    />
                  </Box>
                </Labeled>

                {/* Participant information */}
                <FormDataConsumer>
                  {({ formData }) => {
                    // Parse participant_schedules
                    let schedules = {};
                    try {
                      if (formData.participant_schedules) {
                        // Check if it's already an object or a string
                        if (typeof formData.participant_schedules === 'string') {
                          schedules = JSON.parse(formData.participant_schedules);
                        } else if (typeof formData.participant_schedules === 'object') {
                          schedules = formData.participant_schedules;
                        }
                      }
                    } catch (e) {
                      console.error('Error parsing participant_schedules:', e);
                    }

                    const patronPointsSchedule = schedules["1"] || [];
                    const onPointSchedule = schedules["2"] || [];

                    return (
                      <Box sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                        padding: '1em',
                        marginBottom: '1em'
                      }}>
                        <Labeled label="Participants">
                          <Box sx={{
                            display: 'flex',
                            flexFlow: 'row wrap',
                            gap: '2em'
                          }}>
                            {/* OnPoint Section */}
                            <OnPointSection
                              formData={formData}
                              onPointSchedule={onPointSchedule}
                              selectedSchedule={selectedSchedule}
                              onScheduleChange={setSelectedSchedule}
                            />

                          {/* Patron Points Section */}
                          <Stack sx={{ gap: '0.5em' }}>
                            <BooleanInput
                              source="patron_points_participating"
                              label="Patron Points"
                              readOnly
                            />
                            {formData.patron_points_participating && patronPointsSchedule.length > 0 && (
                              <Box sx={{ pl: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Patron Start Time:
                                </Typography>
                                {patronPointsSchedule.map((date, idx) => (
                                  <Typography key={idx} variant="body2">
                                    {new Date(date).toLocaleString()}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </Stack>
                          </Box>
                        </Labeled>
                      </Box>
                    );
                  }}
                </FormDataConsumer>

                <FormDataConsumer>
                  {({ formData, ...rest }) => (
                    <Labeled label="TC Password Reset">
                      <TextInput source="password" />
                    </Labeled>
                  )}
                </FormDataConsumer>
              </Stack>

              {/* POS settings */}
              <FormDataConsumer>
                {({ formData }) =>
                  <Labeled label="POS Settings">
                    <PosSettings formData={formData}
                    readOnly />
                  </Labeled>
                }
              </FormDataConsumer>
            </Box>
            
            <FormDataConsumer>
              {({ formData }) =>
                <UpsertButton formData={formData} selectedSchedule={selectedSchedule} />
              }
            </FormDataConsumer>
          </Stack>
        </TabbedForm.Tab>
        <TabbedForm.Tab label="Store Logs" path="store-logs">
          <StoreLogsTab />
        </TabbedForm.Tab>
      </TabbedForm>
    </Edit>
  );
};

export default PatronPointsStoreEdit;
